import { useCallback, useRef } from 'react';
import { clientPointToImageCoords } from '../utils/photorealistic/canvasImageCoords';
import {
  clampFlythroughRoi,
} from '../utils/photorealistic/flythroughVideoConfig';

function clientPointToNormalizedImage(clientX, clientY, containerEl, logicalW, logicalH) {
  const point = clientPointToImageCoords(clientX, clientY, containerEl, logicalW, logicalH);
  if (!point) return null;
  return {
    nx: point.x / logicalW,
    ny: point.y / logicalH,
  };
}

function DimRegion({ style }) {
  return (
    <div
      className="pointer-events-none absolute bg-black/40"
      style={style}
      aria-hidden
    />
  );
}

const HANDLE_CLASS =
  'absolute z-10 rounded-sm border-2 border-white bg-primary-300 shadow pointer-events-auto';

function resizeRoiFromHandle(mode, startRoi, point) {
  const right = startRoi.x + startRoi.width;
  const bottom = startRoi.y + startRoi.height;
  const { nx, ny } = point;

  switch (mode) {
    case 'resize-nw':
      return clampFlythroughRoi({ x: nx, y: ny, width: right - nx, height: bottom - ny });
    case 'resize-ne':
      return clampFlythroughRoi({ x: startRoi.x, y: ny, width: nx - startRoi.x, height: bottom - ny });
    case 'resize-sw':
      return clampFlythroughRoi({ x: nx, y: startRoi.y, width: right - nx, height: ny - startRoi.y });
    case 'resize-se':
      return clampFlythroughRoi({ x: startRoi.x, y: startRoi.y, width: nx - startRoi.x, height: ny - startRoi.y });
    case 'resize-n':
      return clampFlythroughRoi({ x: startRoi.x, y: ny, width: startRoi.width, height: bottom - ny });
    case 'resize-s':
      return clampFlythroughRoi({ x: startRoi.x, y: startRoi.y, width: startRoi.width, height: ny - startRoi.y });
    case 'resize-w':
      return clampFlythroughRoi({ x: nx, y: startRoi.y, width: right - nx, height: startRoi.height });
    case 'resize-e':
      return clampFlythroughRoi({ x: startRoi.x, y: startRoi.y, width: nx - startRoi.x, height: startRoi.height });
    default:
      return startRoi;
  }
}

function ResizeHandle({ className, cursorClass, title, ariaLabel, onPointerDown }) {
  return (
    <span
      className={`${HANDLE_CLASS} h-3.5 w-3.5 ${cursorClass} ${className}`}
      title={title}
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
    />
  );
}

const FlythroughRoiCropOverlay = ({
  visible = false,
  containerRef,
  logicalWidth,
  logicalHeight,
  roi,
  onRoiChange,
}) => {
  const interactionRef = useRef({
    mode: null,
    pointerId: null,
    startPoint: null,
    startRoi: null,
  });

  const endInteraction = useCallback(() => {
    interactionRef.current = {
      mode: null,
      pointerId: null,
      startPoint: null,
      startRoi: null,
    };
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      const state = interactionRef.current;
      if (!state.mode || state.pointerId == null || event.pointerId !== state.pointerId) return;

      const container = containerRef?.current;
      if (!container || !state.startPoint || !state.startRoi) return;

      const point = clientPointToNormalizedImage(
        event.clientX,
        event.clientY,
        container,
        logicalWidth,
        logicalHeight,
      );
      if (!point) return;

      const dx = point.nx - state.startPoint.nx;
      const dy = point.ny - state.startPoint.ny;
      const startRoi = state.startRoi;

      if (state.mode === 'move') {
        onRoiChange?.(
          clampFlythroughRoi({
            ...startRoi,
            x: startRoi.x + dx,
            y: startRoi.y + dy,
          }),
        );
        return;
      }

      if (state.mode.startsWith('resize-')) {
        onRoiChange?.(resizeRoiFromHandle(state.mode, startRoi, point));
      }
    },
    [containerRef, logicalWidth, logicalHeight, onRoiChange],
  );

  const startInteraction = useCallback(
    (event, mode) => {
      const container = containerRef?.current;
      const point = container
        ? clientPointToNormalizedImage(
            event.clientX,
            event.clientY,
            container,
            logicalWidth,
            logicalHeight,
          )
        : null;
      if (!point) return;

      interactionRef.current = {
        mode,
        pointerId: event.pointerId,
        startPoint: point,
        startRoi: { ...roi },
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
      event.preventDefault();
      event.stopPropagation();
    },
    [containerRef, logicalWidth, logicalHeight, roi],
  );

  if (!visible || !(logicalWidth > 0) || !(logicalHeight > 0)) {
    return null;
  }

  const safeRoi = clampFlythroughRoi(roi);
  const leftPct = safeRoi.x * 100;
  const topPct = safeRoi.y * 100;
  const widthPct = safeRoi.width * 100;
  const heightPct = safeRoi.height * 100;
  const rightPct = leftPct + widthPct;
  const bottomPct = topPct + heightPct;

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[16] overflow-hidden rounded"
      onPointerMove={handlePointerMove}
      onPointerUp={endInteraction}
      onPointerCancel={endInteraction}
      aria-hidden={!visible}
    >
      <DimRegion style={{ left: 0, top: 0, width: '100%', height: `${topPct}%` }} />
      <DimRegion style={{ left: 0, top: `${topPct}%`, width: `${leftPct}%`, height: `${heightPct}%` }} />
      <DimRegion
        style={{ left: `${rightPct}%`, top: `${topPct}%`, width: `${100 - rightPct}%`, height: `${heightPct}%` }}
      />
      <DimRegion style={{ left: 0, top: `${bottomPct}%`, width: '100%', height: `${100 - bottomPct}%` }} />

      <div
        className="pointer-events-auto absolute touch-none select-none border-2 border-primary-300 shadow-[0_0_0_1px_rgba(255,255,255,0.85)]"
        style={{
          left: `${leftPct}%`,
          top: `${topPct}%`,
          width: `${widthPct}%`,
          height: `${heightPct}%`,
        }}
        onPointerDown={(event) => startInteraction(event, 'move')}
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-y-0 left-1/3 w-px -translate-x-1/2 bg-white/70" />
          <div className="absolute inset-y-0 left-2/3 w-px -translate-x-1/2 bg-white/70" />
          <div className="absolute inset-x-0 top-1/3 h-px -translate-y-1/2 bg-white/70" />
          <div className="absolute inset-x-0 top-2/3 h-px -translate-y-1/2 bg-white/70" />
        </div>

        <ResizeHandle
          className="-left-1.5 -top-1.5"
          cursorClass="cursor-nwse-resize"
          title="Resize top left"
          ariaLabel="Resize crop from top left"
          onPointerDown={(event) => startInteraction(event, 'resize-nw')}
        />
        <ResizeHandle
          className="-right-1.5 -top-1.5"
          cursorClass="cursor-nesw-resize"
          title="Resize top right"
          ariaLabel="Resize crop from top right"
          onPointerDown={(event) => startInteraction(event, 'resize-ne')}
        />
        <ResizeHandle
          className="-bottom-1.5 -left-1.5"
          cursorClass="cursor-nesw-resize"
          title="Resize bottom left"
          ariaLabel="Resize crop from bottom left"
          onPointerDown={(event) => startInteraction(event, 'resize-sw')}
        />
        <ResizeHandle
          className="-bottom-1.5 -right-1.5"
          cursorClass="cursor-nwse-resize"
          title="Resize bottom right"
          ariaLabel="Resize crop from bottom right"
          onPointerDown={(event) => startInteraction(event, 'resize-se')}
        />
        <ResizeHandle
          className="-top-1.5 left-1/2 -translate-x-1/2"
          cursorClass="cursor-ns-resize"
          title="Resize top"
          ariaLabel="Resize crop from top"
          onPointerDown={(event) => startInteraction(event, 'resize-n')}
        />
        <ResizeHandle
          className="-bottom-1.5 left-1/2 -translate-x-1/2"
          cursorClass="cursor-ns-resize"
          title="Resize bottom"
          ariaLabel="Resize crop from bottom"
          onPointerDown={(event) => startInteraction(event, 'resize-s')}
        />
        <ResizeHandle
          className="-left-1.5 top-1/2 -translate-y-1/2"
          cursorClass="cursor-ew-resize"
          title="Resize left"
          ariaLabel="Resize crop from left"
          onPointerDown={(event) => startInteraction(event, 'resize-w')}
        />
        <ResizeHandle
          className="-right-1.5 top-1/2 -translate-y-1/2"
          cursorClass="cursor-ew-resize"
          title="Resize right"
          ariaLabel="Resize crop from right"
          onPointerDown={(event) => startInteraction(event, 'resize-e')}
        />
      </div>
    </div>
  );
};

export default FlythroughRoiCropOverlay;
